// =============================================================================
// components/KpiRules/RulesHelpPanel.jsx
//
// Collapsible theory panel for the Rules page. Explains the CORE CONCEPTS
// of the anomaly detection engine (baselines, detection methods, direction,
// SLA, volume floor, severity, quarantine, incident lifecycle) so an
// operator configuring rules understands what each field actually does and
// why, before they start creating/editing rules. Collapsed by default so it
// never gets in the way of day-to-day rule editing — it's a reference, not
// a wizard.
// =============================================================================
import { useState } from 'react';
import {
	Box, Accordion, AccordionSummary, AccordionDetails,
	Typography, Grid, Divider, Chip,
} from '@mui/material';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import SchoolIcon        from '@mui/icons-material/School';
import TimelineIcon      from '@mui/icons-material/Timeline';
import RuleIcon          from '@mui/icons-material/Rule';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import ShieldIcon        from '@mui/icons-material/Shield';
import FilterAltIcon     from '@mui/icons-material/FilterAlt';
import PriorityHighIcon  from '@mui/icons-material/PriorityHigh';
import HourglassTopIcon  from '@mui/icons-material/HourglassTop';
import AutoDeleteIcon    from '@mui/icons-material/AutoDelete';

// ----------------------------------------------------------------------------
// One theory section: icon + title + body. Body accepts plain text
// (paragraphs split on \n\n) so content stays easy to edit.
// ----------------------------------------------------------------------------
function Section({ icon, title, children }) {
	return (
		<Box sx={{ mb: 2.5 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
				{icon}
				<Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
			</Box>
			<Typography
				variant="body2"
				color="text.secondary"
				sx={{ fontSize: '0.83rem', lineHeight: 1.65, whiteSpace: 'pre-line' }}
			>
				{children}
			</Typography>
		</Box>
	);
}

export default function RulesHelpPanel() {
	const [expanded, setExpanded] = useState(false);

	return (
		<Accordion
			expanded={expanded}
			onChange={(_, isExp) => setExpanded(isExp)}
			variant="outlined"
			sx={{
				mb: 2.5,
				'&:before': { display: 'none' },
				borderColor: expanded ? 'primary.main' : 'divider',
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<SchoolIcon fontSize="small" color="primary" />
					<Typography variant="subtitle2" fontWeight={700}>
						How does this module work? (theory)
					</Typography>
					{!expanded && (
						<Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
							— read this before creating your first rule
						</Typography>
					)}
				</Box>
			</AccordionSummary>

			<AccordionDetails sx={{ pt: 0 }}>
				<Divider sx={{ mb: 2 }} />

				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>

						<Section icon={<TimelineIcon fontSize="small" color="action" />} title="1. The baseline — what 'normal' means for each cell">
							{
								"Every rule tells the engine to learn what 'normal' looks like for a given KPI, "
								+ "cell by cell, from its own recent history — not a single network-wide number. "
								+ "That history becomes the baseline every new reading is compared against.\n\n"
								+ "'Use day of week' controls how that history is built:\n"
								+ "• Yes — the baseline uses the 12 most recent occurrences of the SAME day of the week "
								+ "(e.g. only Fridays), isolating weekly patterns. Recommended for Drop Rate, Throughput, Setup SR, "
								+ "which behave differently on a Friday night than a Sunday morning.\n"
								+ "• No — the baseline uses the last 28 consecutive days. Better for KPIs whose variability "
								+ "doesn't depend on the day of week (Availability, equipment faults, alarms)."
							}
						</Section>

						<Section icon={<RuleIcon fontSize="small" color="action" />} title="2. Detection method — how a deviation is judged">
							{
								"Once a baseline exists, the engine needs a rule for deciding 'is today's value abnormal?'. "
								+ "Three methods are available, and they are NOT interchangeable — each fits a different "
								+ "kind of KPI distribution:\n\n"
								+ "• Percentile (recommended default) — compares the live value against the cell's own "
								+ "historical P95/P5. Robust to skewed, non-normal distributions such as Drop Rate or Block "
								+ "Rate, where most days are near-zero and a handful of bad days pull the average up. The "
								+ "alarm only fires when BOTH the percentile AND the absolute SLA are crossed — the "
								+ "percentile alone can't fire an alarm on a cell whose SLA is not actually breached.\n\n"
								+ "• Threshold (fixed SLA) — ignores history completely; fires purely on the absolute SLA "
								+ "limit. Use only when there's a strict contractual number with no room for interpretation "
								+ "(e.g. Availability must never drop below 98.5%, regardless of what's 'normal' for that cell).\n\n"
								+ "• Z-Score (classic statistical) — compares the value against the historical mean ± 2.5 "
								+ "standard deviations. Works well for KPIs whose distribution is close to a bell curve with "
								+ "low variance, like Availability. Less robust than Percentile on skewed KPIs like Drop Rates, "
								+ "where a normal-distribution assumption doesn't hold."
							}
						</Section>

						<Section icon={<TrendingUpIcon fontSize="small" color="action" />} title="3. Direction — which side of the baseline is 'bad'">
							{
								"Not every KPI degrades the same way, so the engine needs to know which direction is the "
								+ "problem:\n\n"
								+ "• 'A rise indicates a problem' — for KPIs where going UP is bad (Drop Rate, Block Rate, "
								+ "PRB Utilization). The alarm looks for values above the baseline/SLA.\n\n"
								+ "• 'A drop indicates a problem' — for KPIs where going DOWN is bad (Availability, Setup SR, "
								+ "Throughput, CQI). The alarm looks for values below the baseline/SLA.\n\n"
								+ "Getting this wrong doesn't just miss real problems — it can also flood the Incidents "
								+ "module with alarms on days the KPI actually IMPROVED, so it's worth double-checking "
								+ "against the KPI's actual definition."
							}
						</Section>

					</Grid>

					<Grid item xs={12} md={6}>

						<Section icon={<ShieldIcon fontSize="small" color="action" />} title="4. SLA threshold — the contractual floor/ceiling">
							{
								"The SLA Threshold is the absolute, contractual limit for the KPI (e.g. Voice Drop Rate "
								+ "4G = 0.8%, Voice Setup SR = 99.0%, Availability = 98.5%). With Percentile, it works "
								+ "TOGETHER with the statistical comparison (both must fire). With Threshold, it is the "
								+ "ONLY condition — there's no statistical layer on top of it."
							}
						</Section>

						<Section icon={<FilterAltIcon fontSize="small" color="action" />} title="5. Minimum volume — filtering out statistical noise">
							{
								"A cell with almost no traffic can swing between 0% and 100% Drop Rate on a single dropped "
								+ "call — that's noise, not a real problem, and the engine needs to be told to ignore it. "
								+ "'Minimum Volume' is a statistical floor, not a target production volume: the engine "
								+ "always filters using the LARGER of this value and 30% of the cell's own historical "
								+ "average volume — MAX(this value, Avg Historical Volume × 0.30).\n\n"
								+ "So the same '20' setting behaves very differently across the network: on an urban cell "
								+ "(avg. volume 5000) the effective floor becomes 1500, while on a small rural cell "
								+ "(avg. volume 30) it stays at 20. Typical starting points by technology: 2G = 15, "
								+ "3G = 20, 4G = 20, 5G = 15."
							}
						</Section>

						<Section icon={<PriorityHighIcon fontSize="small" color="action" />} title="6. Severity — how urgent the resulting alarm is">
							{
								"Severity is purely operational, not statistical — it doesn't change WHEN an alarm fires, "
								+ "only HOW it's displayed and prioritized once it does, in the Incidents module.\n\n"
								+ "• Critical — direct service impact (Availability, Drop Rates).\n"
								+ "• High — customer-experience impact (Setup SR, Block Rates).\n"
								+ "• Medium — quality indicators (Throughput, CQI)."
							}
						</Section>

					</Grid>
				</Grid>

				<Divider sx={{ my: 2 }} />

				<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
					What happens after a rule fires — the incident lifecycle
				</Typography>

				<Grid container spacing={3}>
					<Grid item xs={12} md={6}>
						<Section icon={<HourglassTopIcon fontSize="small" color="action" />} title="Quarantine — settling in a new/changed baseline">
							{
								"When a rule is created or its detection parameters change, the baseline needs a short "
								+ "window of fresh history before it can be trusted again — comparing today's value "
								+ "against a baseline built from criteria that no longer apply would produce false alarms. "
								+ "During this quarantine window (marked as 'Q. end' on the incident chart), detection for "
								+ "that rule is suppressed; it resumes automatically once enough new history has "
								+ "accumulated."
							}
						</Section>
					</Grid>
					<Grid item xs={12} md={6}>
						<Section icon={<AutoDeleteIcon fontSize="small" color="action" />} title="Open, Closed, and automatic cleanup">
							{
								"An incident stays Open for as long as the KPI keeps breaching the rule; it closes "
								+ "automatically once the KPI returns to normal. Closed incidents are kept for reference "
								+ "but are NOT retained forever: incidents closed more than 30 days ago are purged "
								+ "automatically to keep the Incidents module fast and relevant — this is why the "
								+ "Incidents filters warn that closed items older than 30 days will disappear."
							}
						</Section>
					</Grid>
				</Grid>

				<Divider sx={{ my: 2 }} />

				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
					<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
						Quick picks by KPI type:
					</Typography>
					<Chip size="small" label="Drop / Block Rate → Percentile + rise = problem" variant="outlined" />
					<Chip size="small" label="Setup SR / Throughput → Percentile + drop = problem" variant="outlined" />
					<Chip size="small" label="Availability → Z-Score + drop = problem" variant="outlined" />
					<Chip size="small" label="Hard contractual SLA → Threshold" variant="outlined" />
				</Box>
			</AccordionDetails>
		</Accordion>
	);
}